import { useState } from 'react';
import { Calendar, Clock, MapPin, Link as LinkIcon } from 'lucide-react';

interface EventCardProps {
  title: string;
  date: string;
  description: string;
  imageUrl: string;
  location?: string;
  time?: string; // Legacy field, kept for backward compatibility
  startTime?: string;
  endTime?: string;
  eventLink?: string;
  teacher?: string;
  category?: string;
  registrationLink?: string;

}

export default function EventCard({ 
  title, 
  date, 
  description, 
  imageUrl,
  location,
  time,
  startTime,
  endTime,
  eventLink,
  teacher,
  category,
  registrationLink,
}: EventCardProps) {
  const [imgError, setImgError] = useState(false);

  const getImageUrl = (url: string) => {
    // Log de originele URL voor debugging
    console.log('EventCard - Original image URL:', url);
    
    // Controleer op null of lege string
    if (!url || url.trim() === '') {
      console.log('EventCard - Empty URL, using default');
      return defaultImage;
    }
    
    // Als het een volledige URL is (begint met http of https), gebruik deze direct
    if (url.startsWith('http://') || url.startsWith('https://')) {
      console.log('EventCard - Using full URL:', url);
      return url;
    }
    
    // Als het een URL is die begint met www, voeg https:// toe
    if (url.startsWith('www.')) {
      const fullUrl = `https://${url}`;
      console.log('EventCard - Converted to full URL:', fullUrl);
      return fullUrl;
    }
    
    // Als het pad al begint met /images/, gebruik het zoals het is
    if (url.startsWith('/images/')) {
      console.log('EventCard - Using existing image path:', url);
      return url;
    }
    
    // Anders, bouw een relatieve URL op naar de images folder
    // Verwijder eventuele voorloopslashes om dubbele slashes te voorkomen
    const cleanUrl = url.replace(/^\/+/, '');
    const imagePath = `/images/${cleanUrl}`;
    console.log('EventCard - Created image path:', imagePath);
    return imagePath;
  };

  // Gebruik een standaard fallback afbeelding als er geen URL is of als deze mislukt
  const defaultImage = '/images/aikido-social.jpg';

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl">
      <div className="flex flex-col">
        {/* Header met titel en datum/tijd */}
        <div className="p-4">
          <h3 className="text-xl font-semibold">{title}</h3>
          <div className="flex flex-wrap items-center text-gray-600 mt-1">
            <div className="flex items-center mr-4 mb-1">
              <Calendar className="w-4 h-4 mr-2" />
              <span>{date}</span>
            </div>
            
            {/* Toon ofwel de nieuwe start/eindtijd of de legacy tijd */}
            {(startTime || time) && (
              <div className="flex items-center mr-4 mb-1">
                <Clock className="w-4 h-4 mr-2" />
                <span>
                  {startTime ? startTime : time}
                  {endTime && ` - ${endTime}`}
                  {startTime && ' uur'}
                </span>
              </div>
            )}
            
            {/* Toon category als badge */}
            {category && (
              <span className="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full mb-1">
                {category}
              </span>
            )}
          </div>
          
          {/* Toon docent als die is opgegeven */}
          {teacher && (
            <div className="text-gray-600 text-sm mt-1">
              <span className="font-medium">Docent:</span> {teacher}
            </div>
          )}
        </div>

        {/* Afbeelding */}
        <div className="relative h-72">
          {imageUrl && !imgError ? (
            <img 
              src={getImageUrl(imageUrl)} 
              alt={title} 
              className="absolute inset-0 w-full h-full object-cover"
              onLoad={() => {
                console.log('Image successfully loaded:', imageUrl);
              }}
              onError={(e) => {
                console.error('Image failed to load:', imageUrl);
                console.error('Attempted URL:', getImageUrl(imageUrl));
                setImgError(true);
                const target = e.target as HTMLImageElement;
                target.src = defaultImage;
              }}
            />
          ) : (
            <img 
              src={defaultImage}
              alt={title} 
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => {
                console.error('Even default image failed to load!');
              }}
            />
          )}
        </div>

        {/* Content */}
        <div className="p-6 pt-2">
          {location && (
            <div className="flex items-center text-gray-600 mb-4">
              <MapPin className="w-4 h-4 mr-2" />
              <span>{location}</span>
            </div>
          )}
          <p className="text-gray-600 mb-4">{description}</p>
          <div className="flex flex-wrap gap-4 mt-2">
            {eventLink && (
              <a 
                href={eventLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-primary-600 hover:text-primary-700"
              >
                <LinkIcon className="w-4 h-4 mr-2" />
                Meer informatie
              </a>
            )}
            
            {registrationLink && (
              <a 
                href={registrationLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                Inschrijven
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}